import { DateTime, Interval } from 'luxon';

import {
  DispatchTripRead,
  PayerOrganizationRead,
} from 'src/common/external/bambi-api/bambiApi';
import { TRIP_STATUSES } from 'src/common/tripStatuses';

// This is a buffer on either side of the trip's dropoff/pickup times
const TIME_WINDOW_MINUTES = 60;

/**
 * TODO:
 *
 * We've gone back and forth on TIME_WINDOW_MINUTES.
 * In the current evolution, we split on unassigned trips
 * (dragging and through the menu) and assigned trips
 * (open menu and show Create Multi-load)
 * We may want to split these into separate code paths
 * like getPotentialMultiloadsForUnassignedTrip
 * and getPotentialMultiloadsForAssignedTrip
 *
 * However, want to see how biz rules change further
 * before committing to this or some other approach
 */
export function getPotentialMultiloadTripIds(
  originatingTrip: DispatchTripRead,
  otherTrips: DispatchTripRead[],
  assignmentId: string,
  payers: PayerOrganizationRead[],
  multiLoadId?: string,
  bufferMinutes = TIME_WINDOW_MINUTES
): string[] {
  if (otherTrips.length < 1 || payers.length < 1) {
    return [];
  }

  // If the originating trip should not be multiloaded, do not suggest any trips
  if (originatingTrip.should_avoid_multiload) {
    return [];
  }

  const originatingTripPayer = payers.find(
    (payer) => payer.id === originatingTrip.payer.id
  );

  if (!(originatingTripPayer && originatingTripPayer.can_multiload)) {
    return [];
  }

  const timeInterval = createMultiLoadTimeInterval(
    originatingTrip.scheduled_pickup_at,
    originatingTrip.scheduled_dropoff_at,
    bufferMinutes
  );

  return otherTrips
    .filter((trip) => {
      if (trip.id === originatingTrip.id || trip.id.includes('preview')) {
        return false;
      }

      if (trip.status === TRIP_STATUSES.CANCELED) {
        return false;
      }

      if (trip.should_avoid_multiload) {
        return false;
      }

      if (trip.assignment_id && trip.assignment_id !== assignmentId) {
        return false;
      }

      if (
        typeof multiLoadId === 'string' &&
        trip.multiload_id === multiLoadId
      ) {
        return true;
      }

      const isSamePayerOrHasAgreement =
        trip.payer.id === originatingTrip.payer.id ||
        originatingTripPayer.multiloading_partners?.includes(trip.payer.id);

      if (!isSamePayerOrHasAgreement) {
        return false;
      }

      return (
        timeInterval.contains(DateTime.fromISO(trip.scheduled_pickup_at)) ||
        timeInterval.contains(DateTime.fromISO(trip.scheduled_dropoff_at))
      );
    })
    .map((trip) => trip.id);
}

export function createMultiLoadTimeInterval(
  start: string,
  end: string,
  bufferMinutes = TIME_WINDOW_MINUTES
) {
  return Interval.fromDateTimes(
    DateTime.fromISO(start).minus({ minutes: bufferMinutes }),
    DateTime.fromISO(end).plus({ minutes: bufferMinutes })
  );
}
